import { useEffect } from 'react';
import _ from 'lodash';

import { YES_KEY } from '@/common/constants';

import { useHealthPlanNameStore } from '../../BasicProperties/HealthPlanName/stores';
import {
  CATEGORY_SPEND_LIMIT_ORDER,
  CategorySpendLimitPayload,
  HEALTH_PLAN_TYPE_TO_CATEGORY,
} from '../CoverageLevels.constants';
import { useStore } from '../stores/useCoverageLevels.store';

const mergeCategorySpendLimits = (
  existing: CategorySpendLimitPayload[],
  planIds: string[],
  expectedCategories: string[]
): CategorySpendLimitPayload[] => {
  const merged: CategorySpendLimitPayload[] = [];
  planIds.forEach((planId) => {
    expectedCategories.forEach((catType) => {
      const found = existing.find((e) => e.categoryType === catType && e.planId === planId);
      merged.push(found || { categoryType: catType, categorySpendLimit: '', planId });
    });
  });
  return merged;
};

/**
 * Syncs categorySpendLimits on each coverage level when health plan types,
 * linked plans, or the per-category-spend-limit config changes.
 *
 * Creates one set of category limits per linked plan per coverage level,
 * preserving existing values (including id from the backend).
 */
const useSyncCategorySpendLimits = (isTemplateMode?: boolean) => {
  const healthPlanTypes = useHealthPlanNameStore((state) => state.state.healthPlanTypes);
  const perCategorySpendLimit = useHealthPlanNameStore((state) => state.state.perCategorySpendLimit);
  const linkedPlans = useHealthPlanNameStore((state) => state.state.plans);

  const coverageLevelCount = useStore((state) => state.state.length);

  useEffect(() => {
    if (isTemplateMode) return;
    if (perCategorySpendLimit !== YES_KEY) return;

    const { state, setState } = useStore.getState();
    if (!state.length) return;

    // Build the expected category types in fixed display order
    const expectedCategories = CATEGORY_SPEND_LIMIT_ORDER.filter((hpType) =>
      (healthPlanTypes || []).includes(hpType)
    )
      .map((hpType) => HEALTH_PLAN_TYPE_TO_CATEGORY[hpType]?.categoryType)
      .filter(Boolean);

    if (!expectedCategories.length) return;

    // Get linked plan IDs
    const planIds = _.compact(linkedPlans).map(String);
    if (!planIds.length) return;

    const updatedState = state.map((item) => {
      const existing = item.categorySpendLimits || [];
      const merged = mergeCategorySpendLimits(existing, planIds, expectedCategories);
      return { ...item, categorySpendLimits: merged };
    });

    setState(updatedState);
  }, [healthPlanTypes, perCategorySpendLimit, linkedPlans, isTemplateMode, coverageLevelCount]);
};

export default useSyncCategorySpendLimits;
